import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut, User } from 'lucide-react';
import { useAuthStore } from '@/stores/useAuthStore';
import { authClient } from '@/lib/auth-client';
import { clearQueue } from '@/shared/lib/changeQueue';
import { useToast } from '@/shared/lib/hooks/useToast';

/**
 * SessionWidget — auth session indicator for page headers.
 *
 * Phase 16 CR-02: anonymous users are routed to LandingPage by RequireAuth before
 * they reach any page that mounts SessionWidget. The guest/!userId branch was
 * unreachable dead code (CLAUDE.md rule 11) and has been removed.
 *
 * Two states remain:
 *  1. isPending → null   (no flash during session hydration)
 *  2. signed-in → email indicator + logout button
 *
 * Security (T-10-04): email read from authClient.useSession(), never from useAuthStore.
 * Logout order (T-10-07): clearQueue → signOut → clearUser (never reversed).
 */
export const SessionWidget: FC<{ className?: string }> = ({ className = '' }) => {
  const { t } = useTranslation();
  const { clearUser } = useAuthStore();
  const { data: session, isPending } = authClient.useSession();
  // WR-02: must be at top level (React hook rules) — used in handleLogout catch block
  const { error: toastError } = useToast();

  // AUTH-05: authLoading gate — silent during hydration, prevents stale guest UI flash
  if (isPending) return null;

  // Guard: no session means not authenticated. RequireAuth ensures this component is only
  // mounted in pages reachable by authenticated users, but guard defensively so the component
  // is safe to mount anywhere without rendering stale logout UI.
  if (!session?.user?.id) return null;

  // Signed-in branch
  // T-10-04: email from session only — storing email in Zustand expands XSS-readable surface.
  // WR-04: never fall back to userId (a ULID/UUID) — use a neutral placeholder instead.
  // email is '' during the brief hydration window where Zustand has userId but session.data
  // is still null (independent reactive streams). Next refetch will populate it.
  const email = session?.user?.email ?? '';
  const displayEmail = email
    ? (email.length > 20 ? email.slice(0, 18) + '…' : email)
    : '…'; // loading placeholder, never a UUID

  const handleLogout = async () => {
    try {
      // T-10-07: security order is critical — clear IDB queue BEFORE signOut.
      // If clearUser ran first, userId becomes null but queue still holds prior-user records;
      // the next user's flush() would upload them under the new session.
      await clearQueue();
      await authClient.signOut();
      clearUser();
    } catch (err) {
      // WR-02: surface logout failure to the user — they need to know and can retry.
      // Do NOT call clearUser() here: session cookie may still be valid; clearing Zustand
      // while the cookie remains creates a "logged-out UI, valid cookie" inconsistency.
      console.warn('[SessionWidget] logout failed:', err);
      toastError(t('auth.logout_error', 'Не удалось выйти. Попробуйте снова.'));
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span
        className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 font-medium"
        aria-label={email ? t('auth.logged_in_as', 'Вы вошли как {{email}}', { email }) : undefined}
        title={email ? t('auth.logged_in_as', 'Вы вошли как {{email}}', { email }) : undefined}
      >
        <User size={14} />
        <span className="hidden sm:inline max-w-[120px] truncate">{displayEmail}</span>
      </span>
      <button
        onClick={() => void handleLogout()}
        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-900"
        title={t('auth.logout', 'Выйти')}
        aria-label={t('auth.logout', 'Выйти')}
      >
        <LogOut size={16} />
      </button>
    </div>
  );
};
